import { css } from '@emotion/react';
import { motion } from 'motion/react';
import {
  type FC, type PropsWithChildren, type ReactNode, useCallback, useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { DropdownComponent } from '~/_shared/baseComponents/dropdown';
import { RadioComponent } from '~/_shared/baseComponents/radio/radio.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { useHighlightAnimation } from '~/_shared/hooks/useHighlightAnimation';
import { type Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { boundaryTerritoryGroupInSidebarHighlightFinished } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import type { useBTGFillData } from '../hooks/useBTGFillData';
import { ActiveBoundaryDropdownOptionComponent } from './activeBoundaryDropdownOption.component';
import { ActiveBoundaryDropdownTriggerComponent } from './activeBoundaryDropdownTrigger.component';
import {
  actionsWrapperStyle, headerDropdownContentStyle, headerSizeStyle, headerWrapperStyle, interactiveHeaderWrapperStyle, nameSubheaderStyle, primaryBoundaryLabelStyle,
  trialFlagContainerStyle,
} from './styles';
import { TrialModeWarningComponent } from './trialMode/trialModeWarning.component';

export type BoundaryTerritoryKeySettings = Readonly<{
  isCustom: boolean;
  isEmpty: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onDelete?: () => void;
  onExportData?: () => void;
  onExportContainedBoundaries?: () => void;
  onToggle: () => void;
}>;

export type BoundaryKeySettings = Readonly<{
  isCustom: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onBoundarySettingsClick?: () => void;
  onExportDataInBoundaryClick?: () => void;
  onExportContainedBoundaries?: () => void;
  onManuallyEditPolygonClick?: () => void;
  onRemoveBoundaryClick?: () => void;
  onToggle: () => void;
  onZoomToBoundaryClick: () => void;
}>;

export type ActiveBoundaryProps = Readonly<{
  activeExtraBoundaryFillId?: Uuid | null;
  boundaryIsOptimizedTerritoriesTrial: boolean;
  dropdownProps: Pick<
    ReturnType<typeof useBTGFillData>,
    'allowFillChange' | 'setActiveBTGId' | 'activeBTGOption' | 'filteredBTGDropDownOptions' | 'isOnlyNofillOption'
  >;
  isHighlighted?: boolean;
  isLegendMode?: boolean;
  isPrimary: boolean;
  name: string;
  showActions: boolean;
  toolbar: ReactNode;

  onToggleIsPrimary: () => void;
}>;

const wmsTrialStyle = (theme: Theme) => css({
  border: `2px solid ${theme.textColors.warning}`,
  borderRightWidth: '3px', // scrollbar eats 1px
});

const toolbarWrapperStyle = css({
  minHeight: 34,
});

export const ActiveBoundaryComponent: FC<PropsWithChildren<ActiveBoundaryProps>> = ({
  boundaryIsOptimizedTerritoriesTrial,
  children,
  dropdownProps: {
    allowFillChange,
    activeBTGOption,
    filteredBTGDropDownOptions,
    setActiveBTGId,
    isOnlyNofillOption,
  },
  isHighlighted,
  isLegendMode,
  isPrimary,
  name,
  onToggleIsPrimary,
  showActions,
  toolbar,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    staticHighlightStyles,
    startHighlightAnimation,
    animationScopeRef,
    isAnimationActive,
  } = useHighlightAnimation();

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(status => !status);
  }, []);

  useEffect(() => {
    if (!isHighlighted || !animationScopeRef.current) {
      return;
    }
    if (!isAnimationActive) {
      animationScopeRef.current.scrollIntoView();
      startHighlightAnimation();
    }
    dispatch(boundaryTerritoryGroupInSidebarHighlightFinished());
  }, [isHighlighted, animationScopeRef, isAnimationActive, dispatch, startHighlightAnimation]);

  return (
    <motion.div
      ref={animationScopeRef}
      css={[staticHighlightStyles, boundaryIsOptimizedTerritoriesTrial && wmsTrialStyle]}
    >
      {!isOnlyNofillOption && (
        <div
          css={[
            headerWrapperStyle,
            headerSizeStyle,
            allowFillChange && !isLegendMode ? interactiveHeaderWrapperStyle : undefined,
          ]}
        >
          <DropdownComponent
            css={headerDropdownContentStyle}
            inPortal
            isOpen={allowFillChange && isDropdownOpen}
            triggerComponent={activeBTGOption && (
              <ActiveBoundaryDropdownTriggerComponent
                {...activeBTGOption}
                onClick={
                  (filteredBTGDropDownOptions.length > 0) && !isLegendMode && allowFillChange
                    ? toggleDropdown
                    : undefined
                }
              />
            )}
            onClose={closeDropdown}
          >
            {filteredBTGDropDownOptions.map(btgOption => {
              if (!btgOption) {
                return null;
              }
              return (
                <ActiveBoundaryDropdownOptionComponent
                  key={btgOption.boundaryTerritoryGroup.boundaryTerritoryGroupId}
                  {...btgOption}
                  onClick={() => setActiveBTGId(btgOption.boundaryTerritoryGroup.boundaryTerritoryGroupId)}
                />
              );
            })}
          </DropdownComponent>
        </div>
      )}

      <div css={[nameSubheaderStyle, isOnlyNofillOption ? headerSizeStyle : undefined]}>
        <TooltipComponent
          behavior={TooltipBehavior.ShowOnOverflow}
          delayShow={500}
          tooltipContent={name}
          overflowTolerance={{ x: 0, y: 3 }}
        >
          <LimitNumberOfLinesComponent maxNumberOfLines={2}>
            {name}
          </LimitNumberOfLinesComponent>
        </TooltipComponent>
        {boundaryIsOptimizedTerritoriesTrial && (
          <div css={trialFlagContainerStyle}>
            <TrialModeWarningComponent />
          </div>
        )}
      </div>

      {showActions && (
        <div css={actionsWrapperStyle}>
          <div>
            <RadioComponent
              isChecked={isPrimary}
              onChange={onToggleIsPrimary}
              label={t('Active')}
              css={primaryBoundaryLabelStyle}
              value="active"
            />
          </div>

          <div css={toolbarWrapperStyle}>
            {toolbar}
          </div>
        </div>
      )}
      {children}
    </motion.div>
  );
};
